// Sezione richiudibile della pagina Routing AI: tutte chiuse all'apertura,
// stessa intestazione (icona, titolo, badge, sottotitolo) per ogni fase.
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export type RoutingAccent = 'sky' | 'violet' | 'emerald' | 'amber' | 'indigo' | 'slate';

const ACCENT: Record<RoutingAccent, { border: string; bg: string; text: string; ring: string }> = {
  sky: { border: 'border-sky-200 dark:border-sky-800', bg: 'bg-sky-50 dark:bg-sky-950/20', text: 'text-sky-700 dark:text-sky-300', ring: 'ring-sky-300' },
  violet: { border: 'border-violet-200 dark:border-violet-800', bg: 'bg-violet-50 dark:bg-violet-950/20', text: 'text-violet-700 dark:text-violet-300', ring: 'ring-violet-300' },
  emerald: { border: 'border-emerald-200 dark:border-emerald-800', bg: 'bg-emerald-50 dark:bg-emerald-950/20', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-300' },
  amber: { border: 'border-amber-200 dark:border-amber-800', bg: 'bg-amber-50 dark:bg-amber-950/20', text: 'text-amber-700 dark:text-amber-300', ring: 'ring-amber-300' },
  indigo: { border: 'border-indigo-200 dark:border-indigo-800', bg: 'bg-indigo-50 dark:bg-indigo-950/20', text: 'text-indigo-700 dark:text-indigo-300', ring: 'ring-indigo-300' },
  slate: { border: 'border-slate-200 dark:border-slate-700', bg: 'bg-slate-50 dark:bg-slate-900/30', text: 'text-slate-700 dark:text-slate-200', ring: 'ring-slate-300' },
};

interface RoutingAccordionProps {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  badges?: ReactNode;
  accent: RoutingAccent;
  testId?: string;
  children: ReactNode;
}

export default function RoutingAccordion({ icon: Icon, title, subtitle, badges, accent, testId, children }: RoutingAccordionProps) {
  const a = ACCENT[accent];
  return (
    <details className={`group rounded-lg border ${a.border} ${a.bg} overflow-hidden`} data-testid={testId}>
      <summary className="flex cursor-pointer select-none list-none items-start gap-3 px-4 py-3 transition-colors hover:bg-white/40 dark:hover:bg-black/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md ring-1 ${a.bg} ${a.text} ${a.ring}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={`text-sm font-semibold ${a.text}`}>{title}</h3>
            {badges}
          </div>
          {subtitle && <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{subtitle}</p>}
        </div>
        <span className="mt-1 inline-block text-muted-foreground transition-transform group-open:rotate-90" aria-hidden="true">▸</span>
      </summary>
      <div className="border-t bg-card">{children}</div>
    </details>
  );
}
